import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { MdEmail, MdLock, MdBusiness, MdPerson, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.colors.backgroundLight};
  padding: 24px;
`;

const Card = styled.div`
  background: ${p => p.theme.colors.background};
  border-radius: 16px;
  padding: 48px 40px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.12);
  animation: ${fadeUp} 0.5s ease-out;
`;

const LogoArea = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Logo = styled.div`
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryLight || p.theme.colors.primary});
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin: 0 auto 12px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin: 0 0 6px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${p => p.theme.colors.textSecondary};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.textSecondary};
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${p => p.theme.colors.backgroundLight};
  border: 1.5px solid ${p => p.$error ? '#EF4444' : p.theme.colors.border};
  border-radius: 10px;
  padding: 0 14px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: ${p => p.$error ? '#EF4444' : p.theme.colors.primary};
  }

  svg { color: ${p => p.theme.colors.textMuted || p.theme.colors.textSecondary}; flex-shrink: 0; }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  padding: 12px 0;
  font-size: 14px;
  color: ${p => p.theme.colors.text};
  outline: none;

  &::placeholder { color: ${p => p.theme.colors.textSecondary}; }
`;

const ToggleBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${p => p.theme.colors.textSecondary};
  display: flex;
  align-items: center;
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: #EF4444;
`;

const SubmitBtn = styled.button`
  background: ${p => p.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 14px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  margin-top: 4px;

  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ServerError = styled.div`
  background: #FEE2E2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #B91C1C;
`;

const Footer = styled.p`
  text-align: center;
  font-size: 13px;
  color: ${p => p.theme.colors.textSecondary};
  margin: 20px 0 0;

  a {
    color: ${p => p.theme.colors.primary};
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

export default function Register() {
  const navigate  = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    nombre_empresa: '',
    nombre_admin: '',
    email: '',
    password: '',
    confirm: ''
  });
  const [showPass, setShowPass]  = useState(false);
  const [loading, setLoading]    = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [errors, setErrors]      = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.nombre_empresa.trim()) errs.nombre_empresa = 'Requerido';
    if (!form.email.trim())          errs.email          = 'Requerido';
    if (!form.password)              errs.password       = 'Requerido';
    if (form.password.length < 6)   errs.password       = 'Mínimo 6 caracteres';
    if (form.password !== form.confirm) errs.confirm    = 'Las contraseñas no coinciden';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerErr('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      const res = await authAPI.registro({
        nombre_empresa: form.nombre_empresa.trim(),
        nombre_admin:   form.nombre_admin.trim() || form.nombre_empresa.trim(),
        email:          form.email.trim(),
        password:       form.password
      });

      localStorage.setItem('token', res.token);
      login(res.user, res.token);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setServerErr(err.message || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <LogoArea>
          <Logo>💼</Logo>
          <Title>Crear cuenta</Title>
          <Subtitle>Registra tu empresa y empieza gratis</Subtitle>
        </LogoArea>

        {serverErr && <ServerError>{serverErr}</ServerError>}

        <Form onSubmit={handleSubmit} noValidate>
          <FieldGroup>
            <Label>Nombre de la empresa *</Label>
            <InputWrapper $error={!!errors.nombre_empresa}>
              <MdBusiness size={18} />
              <Input
                type="text"
                name="nombre_empresa"
                placeholder="Ej: Empresa ABC"
                value={form.nombre_empresa}
                onChange={handleChange}
                autoFocus
              />
            </InputWrapper>
            {errors.nombre_empresa && <ErrorText>{errors.nombre_empresa}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <Label>Tu nombre (administrador)</Label>
            <InputWrapper>
              <MdPerson size={18} />
              <Input
                type="text"
                name="nombre_admin"
                placeholder="Opcional — usa nombre de empresa si no lo pones"
                value={form.nombre_admin}
                onChange={handleChange}
              />
            </InputWrapper>
          </FieldGroup>

          <FieldGroup>
            <Label>Email *</Label>
            <InputWrapper $error={!!errors.email}>
              <MdEmail size={18} />
              <Input
                type="email"
                name="email"
                placeholder="tu@correo.com"
                value={form.email}
                onChange={handleChange}
              />
            </InputWrapper>
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <Label>Contraseña *</Label>
            <InputWrapper $error={!!errors.password}>
              <MdLock size={18} />
              <Input
                type={showPass ? 'text' : 'password'}
                name="password"
                placeholder="Mínimo 6 caracteres"
                value={form.password}
                onChange={handleChange}
              />
              <ToggleBtn type="button" onClick={() => setShowPass(p => !p)}>
                {showPass ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
              </ToggleBtn>
            </InputWrapper>
            {errors.password && <ErrorText>{errors.password}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <Label>Confirmar contraseña *</Label>
            <InputWrapper $error={!!errors.confirm}>
              <MdLock size={18} />
              <Input
                type={showPass ? 'text' : 'password'}
                name="confirm"
                placeholder="Repite la contraseña"
                value={form.confirm}
                onChange={handleChange}
              />
            </InputWrapper>
            {errors.confirm && <ErrorText>{errors.confirm}</ErrorText>}
          </FieldGroup>

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? 'Creando cuenta...' : 'Crear cuenta'}
          </SubmitBtn>
        </Form>

        <Footer>
          ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
        </Footer>
      </Card>
    </Container>
  );
}
